import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EmojiPicker } from 'frimousse'
import { LoaderIcon, SearchIcon } from 'lucide-react'
import {
  getFrequentEmoji,
  readSkinTone,
  writeSkinTone,
} from './frequentEmoji'
import type {
  EmojiPickerListCategoryHeaderProps,
  EmojiPickerListEmojiProps,
  EmojiPickerListRowProps,
  SkinTone,
} from 'frimousse'
import { cn } from '@/lib/utils'

const COLUMNS = 8

const sectionLabel =
  'px-2.5 pb-1 pt-2 text-[10.5px] font-semibold uppercase tracking-[0.1em] text-muted-foreground'
const cell =
  'grid h-8 w-9 place-items-center rounded-[7px] text-[17px] leading-none hover:bg-background data-[active]:bg-background'

function Row({ children, ...props }: EmojiPickerListRowProps) {
  return (
    <div {...props} className="flex scroll-my-1 px-1.5">
      {children}
    </div>
  )
}

function Emoji({ emoji, ...props }: EmojiPickerListEmojiProps) {
  return (
    <button {...props} type="button" className={cell}>
      {emoji.emoji}
    </button>
  )
}

function CategoryHeader({ category, ...props }: EmojiPickerListCategoryHeaderProps) {
  return (
    <div {...props} className={cn('bg-popover', sectionLabel)}>
      {category.label}
    </div>
  )
}

interface ReactionPickerProps {
  onSelect: (emoji: string, label?: string) => void
}

/**
 * The reaction picker (design 35b): Frequent first, search by name, the
 * hovered emoji's name and the skin tone in the footer. Built on the frimousse
 * primitives so it can take the design's 8-column grid and section labels
 * without changing the shared emoji picker.
 */
export function ReactionPicker({ onSelect }: ReactionPickerProps) {
  const { t } = useTranslation('common')
  const [query, setQuery] = useState('')
  const [skinTone, setSkinTone] = useState<SkinTone>(() => readSkinTone())
  const frequent = useMemo(() => getFrequentEmoji(COLUMNS), [])

  return (
    <EmojiPicker.Root
      columns={COLUMNS}
      skinTone={skinTone}
      onEmojiSelect={(emoji) => onSelect(emoji.emoji, emoji.label)}
      className="flex h-[360px] w-[300px] flex-col overflow-hidden bg-popover text-popover-foreground"
    >
      <div className="flex h-[38px] shrink-0 items-center gap-2 border-b px-3">
        <SearchIcon className="size-3.5 shrink-0 text-muted-foreground" />
        <EmojiPicker.Search
          autoFocus
          placeholder={t('reactions.searchEmoji')}
          onChange={(event) => setQuery(event.target.value)}
          className="h-full w-full bg-transparent text-[13px] outline-hidden placeholder:text-muted-foreground"
        />
      </div>

      {!query && (
        <>
          <div className={sectionLabel}>{t('reactions.frequent')}</div>
          <div className="flex px-1.5 pb-1.5">
            {frequent.map((emoji) => (
              <button
                key={emoji}
                type="button"
                aria-label={t('reactions.reactWith', { emoji })}
                onClick={() => onSelect(emoji)}
                className={cell}
              >
                {emoji}
              </button>
            ))}
          </div>
        </>
      )}
      <EmojiPicker.Viewport className="relative flex-1 outline-hidden">
        <EmojiPicker.Loading className="absolute inset-0 flex items-center justify-center text-muted-foreground">
          <LoaderIcon className="size-4 animate-spin" />
        </EmojiPicker.Loading>
        <EmojiPicker.Empty className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">
          {t('reactions.noResults')}
        </EmojiPicker.Empty>
        <EmojiPicker.List
          className="select-none pb-1.5"
          components={{ Row, Emoji, CategoryHeader }}
        />
      </EmojiPicker.Viewport>

      <div className="flex h-8 shrink-0 items-center gap-2 border-t bg-chrome px-3 text-xs text-muted-foreground">
        <EmojiPicker.ActiveEmoji>
          {({ emoji }) =>
            emoji ? (
              <>
                <span className="text-[15px] leading-none">{emoji.emoji}</span>
                <span className="min-w-0 truncate">{emoji.label}</span>
              </>
            ) : (
              <span className="min-w-0 truncate">{t('reactions.pickHint')}</span>
            )
          }
        </EmojiPicker.ActiveEmoji>
        <span className="flex-1" />
        <EmojiPicker.SkinTone emoji="✋">
          {({ skinToneVariations }) => (
            <span role="radiogroup" aria-label={t('reactions.skinTone')} className="flex gap-0.5">
              {skinToneVariations.map((variation) => (
                <button
                  key={variation.skinTone}
                  type="button"
                  role="radio"
                  aria-checked={skinTone === variation.skinTone}
                  aria-label={variation.skinTone}
                  onClick={() => {
                    setSkinTone(variation.skinTone)
                    writeSkinTone(variation.skinTone)
                  }}
                  className={cn(
                    'grid size-5 place-items-center rounded text-[13px] leading-none',
                    skinTone === variation.skinTone ? 'bg-secondary' : 'opacity-70 hover:opacity-100',
                  )}
                >
                  {variation.emoji}
                </button>
              ))}
            </span>
          )}
        </EmojiPicker.SkinTone>
      </div>
    </EmojiPicker.Root>
  )
}
